import React, { useRef, useEffect } from 'react';

/**
 * A real-time audio visualizer component using Canvas and Web Audio API AnalyserNode.
 * Renders a circular or linear waveform/frequency visualization.
 */
const AudioVisualizer = ({
    analyser,
    isSpeaking,
    color = '#4f46e5', // Default Indigo
    mode = 'circular', // 'circular' or 'bars'
    className = ""
}) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !analyser) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const renderFrame = () => {
            animationRef.current = requestAnimationFrame(renderFrame);

            analyser.getByteFrequencyData(dataArray);

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (!isSpeaking) return;

            const width = canvas.width;
            const height = canvas.height;
            const centerX = width / 2;
            const centerY = height / 2;

            if (mode === 'circular') {
                const radius = Math.min(width, height) / 3;
                const bars = 64;
                const barWidth = 3;

                for (let i = 0; i < bars; i++) {
                    const index = Math.floor((i / bars) * bufferLength * 0.5); // Use lower frequencies for better visual
                    const value = dataArray[index];
                    const percent = value / 255;
                    const barHeight = percent * radius * 0.8;

                    const angle = (i * (360 / bars) * Math.PI) / 180;
                    const x1 = centerX + Math.cos(angle) * radius;
                    const y1 = centerY + Math.sin(angle) * radius;
                    const x2 = centerX + Math.cos(angle) * (radius + barHeight);
                    const y2 = centerY + Math.sin(angle) * (radius + barHeight);

                    ctx.strokeStyle = color;
                    ctx.lineWidth = barWidth;
                    ctx.lineCap = 'round';
                    ctx.beginPath();
                    ctx.moveTo(x1, y1);
                    ctx.lineTo(x2, y2);
                    ctx.stroke();
                }

                // Inner glow
                const avg = dataArray.reduce((src, val) => src + val, 0) / bufferLength;
                const innerGlow = avg / 255;
                ctx.fillStyle = color;
                ctx.globalAlpha = 0.1 * innerGlow;
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius + (innerGlow * 10), 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = 1.0;
            } else {
                // Bars mode
                const barWidth = (width / bufferLength) * 2.5;
                let barHeight;
                let x = 0;

                for (let i = 0; i < bufferLength; i++) {
                    barHeight = (dataArray[i] / 255) * height;

                    ctx.fillStyle = color;
                    ctx.fillRect(x, height - barHeight, barWidth, barHeight);

                    x += barWidth + 1;
                }
            }
        };

        renderFrame();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [analyser, isSpeaking, color, mode]);

    return (
        <canvas
            ref={canvasRef}
            width={300}
            height={300}
            className={`pointer-events-none ${className}`}
        />
    );
};

export default AudioVisualizer;
